import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableCell,
  TableRow,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../config/api';
import DataTable from '../components/DataTable';

function Prices() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    service_type: '',
    device_type: '',
    amount: '',
    coin_deduction: '',
    description: '',
    recommended: false,
    status: 'active',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/prices');
      setPrices(response.data.data || []);
    } catch (error) {
      console.error('Error fetching prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (price = null) => {
    if (price) {
      setEditing(price);
      setFormData({
        ...price,
        recommended: price.recommended === 1 || price.recommended === true,
      });
    } else {
      setEditing(null);
      setFormData({
        service_type: '',
        device_type: '',
        amount: '',
        coin_deduction: '',
        description: '',
        recommended: false,
        status: 'active',
      });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      service_type: '',
      device_type: '',
      amount: '',
      coin_deduction: '',
      description: '',
      status: 'active',
    });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const dataToSend = {
        ...formData,
        device_type: formData.device_type || null
      };
      if (editing) {
        await api.put(`/prices/${editing.id}`, dataToSend);
      } else {
        await api.post('/prices', dataToSend);
      }
      handleClose();
      fetchPrices();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving price');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this price?')) {
      try {
        await api.delete(`/prices/${id}`);
        fetchPrices();
      } catch (error) {
        console.error('Error deleting price:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Prices</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Price
        </Button>
      </Box>

      <DataTable
        data={prices}
        loading={loading}
        searchFields={['service_type', 'description']}
        emptyMessage="No prices found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'service_type', label: 'Plan Name' },
          { field: 'device_type', label: 'Device Type' },
          { field: 'amount', label: 'Amount' },
          { field: 'coin_deduction', label: 'Coin Deduction' },
          { field: 'description', label: 'Description' },
          { field: 'recommended', label: 'Recommand' },
          { field: 'status', label: 'Status' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(price) => (
          <TableRow key={price.id}>
            <TableCell>{price.id}</TableCell>
            <TableCell>{price.service_type}</TableCell>
            <TableCell>{price.device_type || 'Universal'}</TableCell>
            <TableCell>{price.amount}</TableCell>
            <TableCell>{price.coin_deduction}</TableCell>
            <TableCell>{price.description || '-'}</TableCell>
            <TableCell>
              <Checkbox 
                checked={price.recommended === 1 || price.recommended === true} 
                disabled
              />
            </TableCell>
            <TableCell>{price.status}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleOpen(price)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(price.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Price' : 'Add Price'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <FormControl fullWidth margin="normal" required>
            <TextField
              fullWidth
              label="Plan Name"
              type="text"
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              margin="normal"
              required
            />

          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Device Type</InputLabel>
            <Select
              value={formData.device_type || ''}
              onChange={(e) => setFormData({ ...formData, device_type: e.target.value || null })}
              label="Device Type"
            >
              <MenuItem value="">Universal (All Devices)</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
              <MenuItem value="android">Android</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            margin="normal"
            required
            onWheel={(e) => e.target.blur()}
          />
          <TextField
            fullWidth
            label="Coin Deduction"
            type="number"
            value={formData.coin_deduction}
            onChange={(e) => setFormData({ ...formData, coin_deduction: e.target.value })}
            margin="normal"
            required
            onWheel={(e) => e.target.blur()}
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
            <Checkbox
              checked={formData.recommended === 1 || formData.recommended === true}
              onChange={(e) => setFormData({ ...formData, recommended: e.target.checked })}
            />
            <Typography>Recommended</Typography>
          </Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Prices;

